import { BiddingCsvRowStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import {
  type BidCsvPreviewDataColumn,
  buildBidCsvPreviewColumns,
} from '~/components/bidding/common/bidCsvPreviewColumns'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'

/**
 * 差分プレビュー表の 1 行。通常入札 / ブロック入札で列の中身は違うが、
 * 「行番号 + ステータス + 種別ごとの表示列 + エラー理由」という骨格は同じなので
 * 表示用の共通形に畳んでから 1 つの表に流す (種別ごとに表コンポーネントを
 * 増やさない)。
 */
export type BidCsvPreviewRow = {
  rowNumber: number
  status: BiddingCsvRowStatus
  /** 受渡日 / エリア / 売買 など、 種別ごとの表示列 (すでに整形済み)。 */
  columns: BidCsvPreviewCell[]
  message: string
  /**
   * 既存の札と重複する行への警告 (完成した文言。 spec spot-bid-normal-csv-import.md AC-SPOT-022 /
   * spot-bid-block-csv-import.md AC-DSB-034)。 重複しない行・エラー行は空。 行のステータスは
   * 新規のまま、 取込は止めない。
   */
  warning: string
}

/**
 * 表示列の値。 CSV に書いた値ではなくシステムが組み立てた値 (排出係数紐付けの
 * 判別コメント、 spec spot-bid-normal-csv-import.md AC-SPOT-019 / spot-bid-block-csv-import.md
 * AC-DSB-030) は、 その旨の注記 (`annotation`) を値の下に添えて区別する。
 */
export type BidCsvPreviewCell = string | { text: string; annotation: string }

function renderPreviewCell(
  cell: BidCsvPreviewCell | undefined,
): React.ReactNode {
  if (cell === undefined) return ''
  if (typeof cell === 'string') return cell
  return (
    <VStack align="start" gap={0}>
      <Text as="span">{cell.text}</Text>
      <Text as="span" color="Text.midEmphasis">
        {cell.annotation}
      </Text>
    </VStack>
  )
}

const ROW_STATUS_BADGE: Record<BiddingCsvRowStatus, EnumBadgeSpec> = {
  [BiddingCsvRowStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:spotCsvImport.rowStatus.unspecified',
  },
  [BiddingCsvRowStatus.CREATED]: {
    status: 'info',
    labelKey: 'page:spotCsvImport.rowStatus.created',
  },
  [BiddingCsvRowStatus.ERROR]: {
    status: 'negative',
    labelKey: 'page:spotCsvImport.rowStatus.error',
  },
}

/**
 * 行ステータスのバッジ。 Step2 の差分プレビューと確定時のエラー表示
 * (`ConfirmRejection`) で同じ見た目にするため共有する (Des 回答 #8786)。
 */
export const BidCsvRowStatusBadge = ({
  status,
}: {
  status: BiddingCsvRowStatus
}): React.ReactElement => (
  <EnumStatusBadge map={ROW_STATUS_BADGE} value={status} />
)

/** 種別ごとに変わるデータ列の見出しと揃え (定義は共有の `bidCsvPreviewColumns`)。 */
export type PreviewDataColumn = BidCsvPreviewDataColumn

/**
 * プレビュー表の列定義。 列の骨格 (ステータス + 行番号 + 表示列 + エラー内容) は
 * 時間前入札と共有し (`buildBidCsvPreviewColumns`)、 ここは行ステータスの区分と
 * セルの出し方だけを渡す。
 */
function buildPreviewColumns(
  t: TFunction,
  dataColumns: PreviewDataColumn[],
): Array<MasterDataTableColumn<BidCsvPreviewRow>> {
  return buildBidCsvPreviewColumns<BidCsvPreviewRow>(t, {
    dataColumns,
    renderStatus: (row) => <BidCsvRowStatusBadge status={row.status} />,
    renderDataCell: (row, index) => renderPreviewCell(row.columns[index]),
    // 1 行に複数のエラーがある場合、 backend は改行でつないで返す
    // (ブロック入札 CSV の AC-DSB-022「超えたコマごと」等)。 重複の警告 (エラー行には付かない) は
    // エラーと同じ列に、 警告の色で添える。
    renderMessage: (row) => (
      <VStack align="start" gap={0}>
        {row.message !== '' && (
          <Box as="span" whiteSpace="pre-line">
            {row.message}
          </Box>
        )}
        {row.warning !== '' && (
          <Box as="span" color="Text.warningHigh">
            {row.warning}
          </Box>
        )}
      </VStack>
    ),
    messageHeaderKey: 'page:spotCsvImport.columns.note',
  })
}

type Props = {
  rows: BidCsvPreviewRow[]
  /** `columns` に対応する見出しと揃え (種別ごとに異なる)。 */
  dataColumns: PreviewDataColumn[]
}

/**
 * CSV 取込の差分プレビュー表 (Step2)。
 * 入札 CSV は既存札との突合を行わず全行を「新規」として扱うため、 表示される
 * ステータスは 新規 / エラー の 2 値のみ (spec spot-bid-normal-csv-import.md スコープ内)。
 * 既存の札と重複する行は状態を変えず、 備考の列に警告を添える (AC-SPOT-022 / AC-DSB-034)。
 */
export const PreviewTable = ({
  rows,
  dataColumns,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <MasterDataTable
      columns={buildPreviewColumns(t, dataColumns)}
      getRowKey={(row) => String(row.rowNumber)}
      rows={rows}
    />
  )
}
